import { useEffect, useState } from "react";
import { connect } from "react-redux";

import { Refresh } from "@mui/icons-material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";

import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { deepCopyObject, handleDownloadConversation } from "../../utils/helpers.js";
import AlertDialog from "../dialogs//alertDialog";
import BotpressAssessmentDialog from "../dialogs/botpressAssessmentDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import BotpressInteractiveAssessmentDialog from "../dialogs/botpressInteractiveAssessmentDialog.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import BotpressAssessmentTable from "../ui/botpressAssessmentTable.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressAssessments = ({
  organizationODM,
  botpressBotODM,
  analyticsBotODM,
  userODM,
  assessment,
  conversation,
  searchAssessmentRequest,
  deleteChatRequest,
  assessmentPagination,
  loadConversationRequest,
  conversationFeedback,
  deleteFeedbackRequest,
  updateFeedbackRequest,
  updateAssessmentRequest,
  deleteAssessmentsRequest,
  widgetSize = "full",
  minHeight = "333px",
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [downloadConversation, setDownloadConversation] = useState(null);

  useEffect(() => {
    if (assessment != null) {
      setRows(assessment);
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment]);

  const onRequestNewPage = async pagination => {
    try {
      searchAssessmentRequest(pagination);
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  useEffect(() => {
    if (selectedAssessment != null) {
      loadConversationRequest(selectedAssessment.conversationId);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssessment]);

  const [singleAssessmentEntry, setSingleAssessmentEntry] = useState(null);
  const [singleConversation, setSingleConversation] = useState(null);

  useEffect(() => {
    if (singleAssessmentEntry != null) {
      try {
        const credentialedPayload = {
          botODM: botpressBotODM,
          conversationId: singleAssessmentEntry.conversationId,
        };
        BotpressAPI.loadConversation(credentialedPayload).then(returnedConversation => {
          setSingleConversation(returnedConversation);
        });
      } catch (error) {
        console.error("Error loading conversation for assessment", error);
      }
    } else {
      setSingleConversation(null);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleAssessmentEntry]);

  const [comparisonAssessments, setComparisonAssessments] = useState(null);
  const [comparisonConversationNewest, setComparisonConversationNewest] = useState(null);
  const [comparisonConversationOldest, setComparisonConversationOldest] = useState(null);

  useEffect(() => {
    if (comparisonAssessments != null && comparisonAssessments.length === 2) {
      if (
        comparisonAssessments[0].original.assessmentType !==
        comparisonAssessments[1].original.assessmentType
      ) {
        enqueueSnackbar("You man only compare Assessments of the same type", {
          variant: "error",
        });
        setComparisonConversationNewest(null);
        setComparisonConversationOldest(null);
        return;
      }
      let conversationNewestIndex = null;
      let conversationOldestIndex = null;

      //these are records still
      if (
        new Date(comparisonAssessments[0].createdAt) < new Date(comparisonAssessments[1].createdAt)
      ) {
        conversationOldestIndex = 0;
        conversationNewestIndex = 1;
      } else {
        conversationOldestIndex = 1;
        conversationNewestIndex = 0;
      }

      try {
        const credentialedPayload = {
          botODM: botpressBotODM,
          conversationId: comparisonAssessments[conversationOldestIndex].original.conversationId,
        };
        BotpressAPI.loadConversation(credentialedPayload).then(returnedConversation => {
          setComparisonConversationOldest(returnedConversation);
          const credentialedPayload2 = {
            botODM: botpressBotODM,
            conversationId: comparisonAssessments[conversationNewestIndex].original.conversationId,
          };
          BotpressAPI.loadConversation(credentialedPayload2).then(returnedConversation2 => {
            setComparisonConversationNewest(returnedConversation2);
          });
        });
      } catch (error) {
        console.error("Error loading conversation for assessment", error);
      }
    } else {
      setComparisonConversationNewest(null);
      setComparisonConversationOldest(null);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonAssessments]);

  const onHandleDeveloperLogs = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      const theURL = `https://app.botpress.cloud/workspaces/${botpressBotODM.omniData.workspaceId}/bots/${botpressBotODM.omniData.botId}/conversations/${conversationId}/logs`;
      window.open(theURL, "_blank");

      //got to page.
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  const [deleteRows, setDeleteRows] = useState(null);

  const onDeleteAssessments = rowsIDs => {
    try {
      if (rowsIDs == null) {
        return;
      }
      setDeleteRows(rowsIDs);
    } catch (error) {
      console.error("Error deleting table rows", error);
      setRows([]);
    }
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={"Assessments"}
        text={rows != null ? `${rows.length} Test Conversations` : "Test Conversations"}
      >
        <OmniResponsiveButton
          icon={<Refresh />}
          tooltipTitle="Load this table again"
          buttonText="Refresh"
          onClick={() => {
            let newPagination = deepCopyObject(assessmentPagination);
            newPagination["invalidateCache"] = true;
            onRequestNewPage(newPagination);
          }}
        />
      </WidgetTitle>

      {rows == null && <WidgetSkeleton />}
      {rows != null && (
        <WidgetSurveyContent>
          <BotpressAssessmentTable
            data={rows}
            onEditEntry={setSelectedEntry}
            onRequestNewPage={onRequestNewPage}
            dataReduxPagination={assessmentPagination}
            onViewConversation={setSelectedAssessment}
            onAssessEntry={analyticsBotODM ? setSingleAssessmentEntry : null}
            onCompareAssessments={analyticsBotODM ? setComparisonAssessments : null}
            onDownloadEntry={setDownloadConversation}
            onHandleDeveloperLogs={onHandleDeveloperLogs}
            onDeleteAssessments={onDeleteAssessments}
            supportedAssessmentTypes={organizationODM.omniData.supportedAssessmentTypes}
          />
        </WidgetSurveyContent>
      )}

      {selectedEntry != null && (
        <BotpressAssessmentDialog
          open={selectedEntry != null}
          assessmentRecord={selectedEntry}
          onAccept={theRecord => {
            updateAssessmentRequest({ assessmentRecord: theRecord });
            setSelectedEntry(null);
          }}
          onDelete={theRecord => {
            deleteAssessmentsRequest({ assessmentIDs: [theRecord.id] });
            setSelectedEntry(null);
          }}
          onClose={() => {
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedAssessment != null && conversation != null && (
        <BotpressConversationDialog
          conversationId={conversation.conversationId}
          conversation={conversation}
          dialogTitle={`${selectedAssessment.assessmentType.includes("#") ? "Rerun of " + selectedAssessment.assessmentType : selectedAssessment.assessmentName}`}
          conversationFeedback={conversationFeedback}
          deleteFeedbackRequest={deleteFeedbackRequest}
          updateFeedbackRequest={updateFeedbackRequest}
          userODM={userODM}
          open={selectedAssessment != null}
          defaultValue={selectedAssessment}
          onAccept={() => {
            loadConversationRequest(null);
            setSelectedAssessment(null);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setSelectedAssessment(null);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: selectedAssessment.conversationId });
            loadConversationRequest(null);
            setSelectedAssessment(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
          }}
        />
      )}

      {singleConversation != null && (
        <BotpressInteractiveAssessmentDialog
          userODM={userODM}
          analyticsBotODM={analyticsBotODM}
          open={singleConversation != null}
          singleConversation={singleConversation}
          onAccept={() => {
            setSingleAssessmentEntry(null);
          }}
          onClose={() => {
            setSingleAssessmentEntry(null);
          }}
        />
      )}

      {comparisonConversationNewest != null && comparisonConversationOldest != null && (
        <BotpressInteractiveAssessmentDialog
          userODM={userODM}
          analyticsBotODM={analyticsBotODM}
          open={comparisonConversationNewest != null && comparisonConversationOldest != null}
          newestConversation={comparisonConversationNewest}
          oldestConversation={comparisonConversationOldest}
          onAccept={() => {
            setComparisonConversationNewest(null);
            setComparisonConversationOldest(null);
          }}
          onClose={() => {
            setComparisonConversationNewest(null);
            setComparisonConversationOldest(null);
          }}
        />
      )}

      {downloadConversation != null && (
        //todo replace this with a new dialog that supports a download format property
        <TextInputDialog
          inputLabel={`File Name`}
          open={downloadConversation}
          dialogTitle={"Download Conversation"}
          dialogMessage={`Please enter a file name for the conversation download`}
          defaultValue={`Conversation_${format(new Date(), "yyMMdd_hhmm")}`}
          inputPlaceholder={"File Name Here"}
          onAccept={theValue => {
            try {
              BotpressAPI.loadConversation({
                botODM: botpressBotODM,
                conversationId: downloadConversation.conversationId,
              }).then(returnedMessages => {
                handleDownloadConversation(returnedMessages.reverse(), theValue.split(".")[0]);
              });

              setDownloadConversation(null);
            } catch (error) {
              console.log(error);
            }
            setDownloadConversation(null);
          }}
          onClose={() => {
            setDownloadConversation(null);
          }}
        />
      )}

      {deleteRows && (
        <AlertDialog
          open={deleteRows}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete these Assessments?"}
          onAccept={() => {
            deleteAssessmentsRequest({ assessmentIDs: deleteRows });
            setDeleteRows(null);
          }}
          onClose={() => {
            setDeleteRows(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  assessment: state.botpressSession.assessment,
  assessmentsStartDate: state.botpressSession.assessmentsStartDate,
  assessmentsEndDate: state.botpressSession.assessmentsEndDate,
  assessmentPagination: state.botpressSession.assessmentPagination,

  conversation: state.botpressSession.conversation,
  conversationFeedback: state.botpressSession.conversationFeedback,

  userODM: state.userSession.userODM,

  botpressBotODM: state.botpressSession.botpressBotODM,
  analyticsBotODM: state.botpressSession.analyticsBotODM,

  organizationODM: state.userSession.organizationODM,
});

const mapDispatchToProps = {
  searchAssessmentRequest: BotpressSessionActions.searchAssessmentRequest,
  deleteChatRequest: BotpressSessionActions.deleteChatRequest,
  loadConversationRequest: BotpressSessionActions.loadConversationRequest,
  deleteFeedbackRequest: BotpressSessionActions.deleteFeedbackRequest,
  updateFeedbackRequest: BotpressSessionActions.updateFeedbackRequest,
  deleteAssessmentsRequest: BotpressSessionActions.deleteAssessmentsRequest,
  updateAssessmentRequest: BotpressSessionActions.updateAssessmentRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressAssessments);
